import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The desktop sidebar: pinned open (it takes its own space and the page sits beside it) or
 * unpinned (a slim icon rail; hovering slides it open OVER the page, like macOS, and it tucks
 * away when the pointer leaves). The pinned choice is remembered per browser and toggled with
 * ⌘\ / Ctrl+\. Layout follows `html[data-sidebar]`, so the content column and the frame only
 * move when pinning changes, never while peeking.
 */
const KEY = 'tapwork:sidebar:collapsed:v1'

function readPinned(): boolean {
  try {
    return localStorage.getItem(KEY) !== '1'
  } catch {
    return true
  }
}

export function useSidebarCollapsed(behavior: 'peek' | 'dock' = 'peek') {
  const [pinned, setPinned] = useState(readPinned)
  const [peek, setPeek] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    document.documentElement.dataset.sidebar = pinned ? 'expanded' : 'collapsed'
    try {
      localStorage.setItem(KEY, pinned ? '0' : '1')
    } catch {
      // Storage optional.
    }
  }, [pinned])

  const setPinnedTo = useCallback((next: boolean) => {
    window.clearTimeout(timer.current)
    setPeek(false)
    setPinned(next)
  }, [])

  const togglePin = useCallback(() => {
    window.clearTimeout(timer.current)
    setPeek(false)
    setPinned((p) => !p)
  }, [])

  useEffect(() => {
    let last = 0
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== '\\') return
      e.preventDefault()
      // Holding the keys auto-repeats on macOS; one press is one toggle, and a new toggle waits
      // for the previous animation (420ms) to land so it can never flicker.
      const now = performance.now()
      if (e.repeat || now - last < 450) return
      last = now
      togglePin()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [togglePin])

  /** Hover intent: a short wait before opening (so passing over it does nothing), a longer one before closing. */
  const peekOn = useCallback(() => {
    if (behavior === 'dock') return
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setPeek(true), 140)
  }, [behavior])
  const peekOff = useCallback(() => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setPeek(false), 260)
  }, [])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const peeking = !pinned && peek && behavior === 'peek'
  return { pinned, peek: peeking, open: pinned || peeking, dock: !pinned && behavior === 'dock', togglePin, setPinned: setPinnedTo, peekOn, peekOff }
}
